import { useRef } from 'react';
import {
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/* ----- styled helpers --------------------------------------------------- */

const CarouselShell = styled(Box)({
    width: '100%',
    position: 'relative',
});

const Viewport = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    padding: theme.spacing(1, 0),
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': { display: 'none' },      // hide bar
}));

const Card = styled(Box)(({ theme }) => ({
    flex: '0 0 auto',
    scrollSnapAlign: 'start',
    textDecoration: 'none',
    color: theme.palette.text.primary,                // << no blue
    '&:visited, &:link': { color: theme.palette.text.primary },

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    background: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(3),
    transition: 'all .3s',

    '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[4] },

    '& img': {
        width: 64,
        height: 64,
        objectFit: 'contain',
        marginBottom: theme.spacing(1),
        transition: 'transform .3s',
    },
    '&:hover img': { transform: 'scale(1.1)' },
}));

const Arrow = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    '&:hover': { background: theme.palette.background.paper },
}));

/* ----- component -------------------------------------------------------- */

export default function CategoryCarousel({ categories = [] }) {
    const viewportRef = useRef(null);

    const scrollBy = (dir) =>
        viewportRef.current?.scrollBy({
            left: dir * 220,   // ≈ one card
            behavior: 'smooth',
        });

    if (!categories.length) return null;

    return (
        <CarouselShell>
            {/* ← arrow */}
            <Arrow
                aria-label="previous"
                onClick={() => scrollBy(-1)}
                sx={{ left: -12 }}
            >
                <ChevronLeftIcon />
            </Arrow>

            {/* cards */}
            <Viewport ref={viewportRef}>
                {categories.map((cat) => (
                    <Card
                        key={cat.id}
                        component={RouterLink}
                        to={`/listings?category=${encodeURIComponent(cat.id)}`}
                    >
                        <img
                            src={cat.logo_url || `/icons/${cat.id}.png`}
                            alt={cat.name}
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                            {cat.name}
                        </Typography>
                    </Card>
                ))}
            </Viewport>

            {/* → arrow */}
            <Arrow
                aria-label="next"
                onClick={() => scrollBy(1)}
                sx={{ right: -12 }}
            >
                <ChevronRightIcon />
            </Arrow>
        </CarouselShell>
    );
}
